import { useState, useCallback } from "react";

interface RateLimitState {
  attempts: number;
  lockedUntil: number | null;
  lastAttempt: number | null;
}

interface UseRateLimitOptions {
  maxAttempts?: number;
  lockoutDuration?: number; // in milliseconds
  storageKey?: string;
}

interface UseRateLimitReturn {
  isLocked: boolean;
  remainingAttempts: number;
  lockoutTimeRemaining: number;
  recordAttempt: (success: boolean) => void;
  resetAttempts: () => void;
  canAttempt: () => boolean;
}

const DEFAULT_MAX_ATTEMPTS = 5;
const DEFAULT_LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes

export function useRateLimit(options: UseRateLimitOptions = {}): UseRateLimitReturn {
  const {
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    lockoutDuration = DEFAULT_LOCKOUT_DURATION,
    storageKey = "auth_rate_limit",
  } = options;

  const getStoredState = (): RateLimitState => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const state = JSON.parse(stored) as RateLimitState;
        // Check if lockout has expired
        if (state.lockedUntil && Date.now() > state.lockedUntil) {
          return { attempts: 0, lockedUntil: null, lastAttempt: null };
        }
        return state;
      }
    } catch {
      // Ignore parsing errors
    }
    return { attempts: 0, lockedUntil: null, lastAttempt: null };
  };

  const [state, setState] = useState<RateLimitState>(getStoredState);

  const saveState = useCallback((newState: RateLimitState) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(newState));
    } catch {
      // Ignore storage errors
    }
    setState(newState);
  }, [storageKey]);

  const isLocked = state.lockedUntil ? Date.now() < state.lockedUntil : false;
  const remainingAttempts = Math.max(0, maxAttempts - state.attempts);
  const lockoutTimeRemaining = state.lockedUntil 
    ? Math.max(0, state.lockedUntil - Date.now()) 
    : 0;

  const canAttempt = useCallback((): boolean => {
    const currentState = getStoredState();
    if (currentState.lockedUntil && Date.now() < currentState.lockedUntil) {
      return false;
    }
    return true;
  }, []);

  const recordAttempt = useCallback((success: boolean) => {
    if (success) {
      // Reset on successful attempt
      saveState({ attempts: 0, lockedUntil: null, lastAttempt: Date.now() });
      return;
    }

    const currentState = getStoredState();
    const newAttempts = currentState.attempts + 1;
    
    if (newAttempts >= maxAttempts) {
      // Lock out the user
      saveState({
        attempts: newAttempts,
        lockedUntil: Date.now() + lockoutDuration,
        lastAttempt: Date.now(),
      });
    } else {
      saveState({
        attempts: newAttempts,
        lockedUntil: null,
        lastAttempt: Date.now(),
      });
    }
  }, [maxAttempts, lockoutDuration, saveState]);

  const resetAttempts = useCallback(() => {
    saveState({ attempts: 0, lockedUntil: null, lastAttempt: null });
  }, [saveState]);

  return {
    isLocked,
    remainingAttempts,
    lockoutTimeRemaining,
    recordAttempt,
    resetAttempts,
    canAttempt,
  };
}

export function formatLockoutTime(ms: number): string {
  const seconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}
